import { Component, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { getLoggerFor } from "@tylertech/logging";
import { ExecutionResult, IAppRegistrationModel, LoginViewModel } from "@tylertech/tyler-api-types";
import { AppService, SecurityService } from "../../../services";

const enum NineBoxSize
{
    Default = 4,
    More = 15,
}

@Component({
    selector: "nine-box",
    templateUrl: "./nineBox.tmpl.html",
    styleUrls: [
        "./nineBox.component.scss"
    ],
    standalone: false
})
export class NineBoxComponent
{
    private readonly $securityService = inject(SecurityService);
    private readonly $appService = inject(AppService);

    private readonly log = getLoggerFor("NineBoxComponent");
    moreApps: Array<Array<IAppRegistrationModel>> = [];
    mainApps: Array<Array<IAppRegistrationModel>> = [];
    showMoreOption: boolean;
    showAll: boolean;
    isLoaded: boolean;
    authenticated: boolean;

    constructor()
    {
        this.$securityService.principleChange
            .pipe(takeUntilDestroyed())
            .subscribe((_newUser: LoginViewModel) =>
            {
                this.mainApps.length = 0;
                this.moreApps.length = 0;
                this.authenticated = this.$securityService.isAuthenticated;
                if (!this.authenticated)
                {
                    return;
                }

                this.isLoaded = false;
                this.fetchApplications()
                    .finally(() =>
                    {
                        this.isLoaded = true;
                    });
            });
    }


    get nineBoxClasses(): Record<string, boolean>
    {
        return {
            "scrollItems": this.showAll,
            "hideItems": !this.showAll
        };
    }

    protected fetchApplications(): Promise<void>
    {
        return this.$appService.RegisterModules()
            .then(
                (executionResult: ExecutionResult<Array<IAppRegistrationModel>>) =>
                {
                    const applications = executionResult.Result;

                    const rows = chunk(applications, 4);

                    const appCount = Math.min(rows.length, NineBoxSize.Default);
                    this.mainApps = rows.slice(0, appCount);

                    this.showMoreOption = rows.length > NineBoxSize.Default;

                    if (rows.length <= NineBoxSize.Default)
                    {
                        this.moreApps = [];
                        return;
                    }

                    const moreAppsCount = rows.length > NineBoxSize.Default + NineBoxSize.More
                        ? NineBoxSize.More
                        : rows.length - NineBoxSize.Default;
                    this.moreApps = rows.slice(NineBoxSize.Default, NineBoxSize.More + moreAppsCount);

                },
                error =>
                {
                    this.log.error("Error retrieving registered modules.", error);
                });
    }

    onShowMoreClick($event?: Event): void
    {
        $event.stopPropagation();

        this.showMoreOption = false;
        this.showAll = true;
    }

}

function chunk<T>(array: Array<T>, size: number): Array<Array<T>>
{
    const newArr = [];
    for (let i = 0; i < array.length; i += size)
    {
        newArr.push(array.slice(i, i + size));
    }
    return newArr;
}
